import { Button, Input } from "@jhool-io/fe-components";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { sendPasswordResetEmail } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import * as Sentry from "@sentry/react";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import useToast from "../../../hooks/useToast";
import { auth } from "../../../firebase";

interface IResetPasswordProps {
    email: string;
}

const schema = yup.object({
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
});

export default function ResetPasswordPage() {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<IResetPasswordProps>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const navigate = useNavigate();

    const { toast } = useToast();

    const showPasswordResetConfirmation = () => {
        toast({
            mode: "success",
            message:
                "If an account exists for that email, a password reset link has been sent.",
        });
        navigate("/login");
    };

    const onSubmit = async (data: IResetPasswordProps) => {
        try {
            await sendPasswordResetEmail(auth, data.email);
            showPasswordResetConfirmation();
        } catch (error) {
            if (error instanceof FirebaseError) {
                if (error.code === "auth/user-not-found") {
                    showPasswordResetConfirmation();
                    return;
                }
                Sentry.logger.error("Password reset email send failed", {
                    error_code: error.code,
                });
                toast({
                    mode: "error",
                    message:
                        "Could not send password reset email, please try again later",
                });
            } else {
                Sentry.logger.error("Password reset email send failed", {
                    error_message:
                        error instanceof Error
                            ? error.message
                            : "Unknown error",
                });
                toast({
                    mode: "error",
                    message:
                        "Could not send password reset email, please try again later",
                });
            }
        }
    };

    return (
        <AuthWrapper
            footer={
                <div className="text-center">
                    <p className="text-textSec font-normal text-base tracking-[-0.154px]">
                        Remember password?{" "}
                        <Link className="link font-medium" to="/login">
                            Login
                        </Link>
                    </p>
                </div>
            }
        >
            <>
                <h3 className="text-titleLg font-bold text-text mb-[--spacing-items]">
                    Reset Password
                </h3>
                <p className="text-textSec tracking-[-0.176px] font-normal">
                    We will send an email with verification link. If you don’t
                    see it, please check your spam folder
                </p>
                <form
                    className="mt-[--spacing-form-items]"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className="mb-[--spacing-form-items]">
                        <Input
                            {...register("email")}
                            label="Email Address"
                            inputSize="medium"
                            placeholder="Enter your email address"
                            hasError={!!errors.email}
                            errorText={errors.email?.message}
                        />
                    </div>
                    <Button
                        disabled={isSubmitting}
                        className="w-full"
                        size="medium"
                        type="submit"
                    >
                        Send email
                    </Button>
                </form>
            </>
        </AuthWrapper>
    );
}
